// packages/web/src/components/teach/ExerciseLibraryTab.tsx
import React, { useEffect, useState } from 'react';
import { useExerciseLibraryStore } from '../../stores/exerciseLibraryStore';
import { Trash2, Plus, CheckSquare, Square } from 'lucide-react';
import { ExerciseLibraryTable } from './ExerciseLibraryTable';
import { ExerciseDetailModal } from './ExerciseDetailModal';
import { AddToHomeworkModal } from './AddToHomeworkModal';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { Toast } from '../common/Toast';
import { useToast } from '../../hooks/useToast';

export const ExerciseLibraryTab: React.FC = () => {
  const {
    exercises,
    loading,
    error,
    categoryFilter,
    assignmentStatusFilter,
    fetchExercises,
    setCategoryFilter,
    setAssignmentStatusFilter,
    selectAllExercises,
    deselectAllExercises,
    deleteExercises,
    setShowAddToHomeworkModal,
    showDetailModal,
    showAddToHomeworkModal,
  } = useExerciseLibraryStore();

  const [showBatchDeleteConfirm, setShowBatchDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { toast, success, error: showError, warning, hideToast } = useToast();

  useEffect(() => {
    fetchExercises();
  }, []);

  const selectedExercises = exercises.filter(ex => ex.selected);
  const allSelected = exercises.length > 0 && selectedExercises.length === exercises.length;

  const handleBatchDelete = () => {
    if (selectedExercises.length === 0) return;
    setShowBatchDeleteConfirm(true);
  };

  const confirmBatchDelete = async () => {
    setDeleting(true);
    try {
      await deleteExercises(selectedExercises.map(ex => ex.id));
      success('删除成功');
      setShowBatchDeleteConfirm(false);
    } catch (err) {
      showError('删除失败');
    } finally {
      setDeleting(false);
    }
  };

  const handleAddToHomework = () => {
    if (selectedExercises.length === 0) {
      warning('请先选择习题');
      return;
    }
    setShowAddToHomeworkModal(true);
  };

  const handleToggleSelectAll = () => {
    if (allSelected) {
      deselectAllExercises();
    } else {
      selectAllExercises();
    }
  };

  return (
    <div className="exercise-library-tab">
      {/* 筛选栏 */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* 科目筛选 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">科目</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="all">全部</option>
              <option value="math">数学</option>
              <option value="physics">物理</option>
              <option value="chemistry">化学</option>
              <option value="others">其他</option>
            </select>
          </div>

          {/* 布置状态筛选 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">布置状态</label>
            <select
              value={assignmentStatusFilter}
              onChange={(e) => setAssignmentStatusFilter(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="all">全部</option>
              <option value="assigned">已布置</option>
              <option value="unassigned">未布置</option>
            </select>
          </div>

          {/* 创建时间筛选 - 预留 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">创建时间</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              disabled
            >
              <option>全部时间</option>
            </select>
          </div>
        </div>
      </div>

      {/* 批量操作栏 */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handleToggleSelectAll}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
            >
              {allSelected ? (
                <CheckSquare className="w-4 h-4 text-blue-600" />
              ) : (
                <Square className="w-4 h-4" />
              )}
              {allSelected ? '取消全选' : '全选'}
            </button>
            
            {selectedExercises.length > 0 && (
              <span className="text-sm text-gray-600">
                已选择 {selectedExercises.length} 个习题
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleAddToHomework}
              disabled={selectedExercises.length === 0}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              <Plus className="w-4 h-4" />
              加入作业
            </button>

            <button
              onClick={handleBatchDelete}
              disabled={selectedExercises.length === 0}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              批量删除
            </button>
          </div>
        </div>
      </div>

      {/* 加载状态 */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-sm text-gray-600">加载中...</p>
        </div>
      )}

      {/* 错误提示 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* 习题列表 */}
      {!loading && exercises.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <p className="text-gray-500">暂无习题</p>
        </div>
      )}

      {!loading && exercises.length > 0 && <ExerciseLibraryTable />}

      {/* 模态窗口 */}
      {showDetailModal && <ExerciseDetailModal />}
      {showAddToHomeworkModal && <AddToHomeworkModal />}

      {/* 批量删除确认对话框 */}
      <ConfirmDialog
        isOpen={showBatchDeleteConfirm}
        title="确认批量删除"
        message={`确定要删除选中的 ${selectedExercises.length} 个习题吗？已布置的习题将被跳过。`}
        confirmText="删除"
        cancelText="取消"
        onConfirm={confirmBatchDelete}
        onCancel={() => setShowBatchDeleteConfirm(false)}
        isLoading={deleting}
        type="danger"
      />

      {/* Toast提示 */}
      {toast.isOpen && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
    </div>
  );
};
